import React from 'react';
import { AlertTriangle } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';

interface Props {
  open: boolean;
  talentName: string;
  onConfirm: () => void;
  onCancel: () => void;
  loading?: boolean;
}

const TalentDeleteConfirmationDialog: React.FC<Props> = ({
  open,
  talentName,
  onConfirm,
  onCancel,
  loading = false,
}) => {
  return (
    <Dialog
      open={open}
      onOpenChange={(next) => { if (!next) onCancel(); }}
    >
      <DialogContent
        className="sm:max-w-[500px]"
        data-testid="talent-delete-confirmation-dialog"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-amber-500" />
            Bekreft sletting av talent
          </DialogTitle>
          <DialogDescription className="sr-only">
            Bekreft at du vil fjerne talentet {talentName} fra produksjonen.
          </DialogDescription>
        </DialogHeader>

        <Alert variant="destructive" className="border-amber-200 bg-amber-50 text-amber-900 [&>svg]:text-amber-600">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>
            Du er i ferd med å fjerne talentet fra produksjonen.
          </AlertDescription>
        </Alert>

        <p className="text-sm text-foreground">
          <strong>Talent:</strong> {talentName}
        </p>

        <p className="text-sm text-muted-foreground">
          Dette vil fjerne talentet helt fra produksjonen. Alle tilknyttede bemanning vil også bli fjernet.
          Denne handlingen kan ikke angres.
        </p>

        <DialogFooter className="gap-2">
          <Button
            onClick={onCancel}
            disabled={loading}
            variant="outline"
            data-testid="cancel-button"
          >
            Avbryt
          </Button>
          <Button
            onClick={onConfirm}
            disabled={loading}
            variant="destructive"
            data-testid="confirm-delete-button"
          >
            {loading ? 'Sletter...' : 'Slett talent'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default TalentDeleteConfirmationDialog;
